import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Marquee from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { useSeo, JsonLd } from "@/lib/seo";
import { track } from "@/lib/track";

const FRUITS = [
  {
    name: "Alphonso Mango",
    season: "Summer",
    copy: "The king of mangoes. When Alphonso season arrives, the menu bends to it — real pulp, real perfume, no shortcuts.",
    img: IMG.ripe.alphonso,
  },
  {
    name: "Strawberry",
    season: "Winter",
    copy: "Sweet, sharp and gone too soon. Winter strawberries picked at their reddest go straight into the scoop.",
    img: IMG.ripe.strawberry,
  },
  {
    name: "Sitaphal",
    season: "Monsoon",
    copy: "Custard apple creaminess that no flavouring has ever managed to copy. We wait for it every year.",
    img: IMG.ripe.sitaphal,
  },
  {
    name: "Tender Coconut",
    season: "All Seasons",
    copy: "Cool, clean and subtly sweet. The gentle one that never leaves the menu.",
    img: IMG.ripe.coconut,
  },
];

export default function RipeNow() {
  useSeo({
    title: "Ripe Now | RIFIC — Seasonal Real Fruit Ice Cream",
    description:
      "RIPE NOW is RIFIC's seasonal platform — Alphonso mango, strawberry, sitaphal and tender coconut flavours that follow the fruit calendar. We pick it ripe, we make it RIFIC.",
    path: "/ripe-now",
  });

  useEffect(() => track("ripe_now_view"), []);

  return (
    <div data-testid="ripe-now-page">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is RIPE NOW?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "RIPE NOW is RIFIC's seasonal platform — fruit-led flavours that follow the natural fruit calendar instead of a fixed menu.",
              },
            },
          ],
        }}
      />
      <PageHeader
        eyebrow="Ripe Now"
        title={["WE PICK IT RIPE.", "WE MAKE IT RIFIC."]}
        copy="RIPE NOW is our seasonal platform — a promise that our fruit-led flavours follow the fruit calendar, not a spreadsheet. When the fruit is at its best, that's when it lands in your scoop."
        dark
      />

      <section className="bg-deep pb-16 md:pb-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          {FRUITS.map((f, i) => (
            <Reveal key={f.name}>
              <div
                data-testid={`ripe-now-feature-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`grid items-center gap-8 border-t border-deep-line py-10 md:py-14 lg:grid-cols-2 lg:gap-14 ${
                  i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="overflow-hidden rounded-[28px] bg-cream">
                  <img src={f.img} alt={`RIFIC ${f.name} — ${f.season.toLowerCase()} real fruit ice cream`} loading="lazy" className="h-auto w-full object-contain" />
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">{f.season}</p>
                  <h2 className="mt-3 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold uppercase tracking-[-0.01em] text-cream">{f.name}</h2>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-cream/80 md:text-lg">{f.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee
        className="bg-orange-rific py-4 text-deep md:py-5"
        textClassName="font-display text-xl md:text-2xl font-bold uppercase tracking-[-0.01em]"
        items={["WE PICK IT RIPE.", "WE MAKE IT RIFIC.", "WE PICK IT RIPE.", "WE MAKE IT RIFIC."]}
      />

      <section className="bg-cream py-16 text-center md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal>
            <p className="font-display text-2xl font-bold tracking-[-0.01em] text-deep md:text-4xl">
              Good fruit has a season.
              <br />
              <span className="text-orange-rific">We think ice cream should respect it.</span>
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/menu"
                data-testid="ripe-now-menu-cta"
                className="inline-flex items-center rounded-full bg-deep px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:bg-orange-rific"
              >
                Explore the Menu
              </Link>
              <Link
                to="/stores"
                data-testid="ripe-now-stores-cta"
                className="inline-flex items-center rounded-full border-2 border-deep px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-deep transition-colors duration-300 hover:bg-deep hover:text-cream"
              >
                Find a Store
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
